import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  AlertTriangle, RefreshCw, ChevronRight, KeyRound, ServerCrash,
  AlertCircle, ExternalLink, Loader2, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';

// Visible only when the current user is a super admin AND failures exist.
// Designed to live INSIDE Super Admin → Bills → Dashboard as a single tile.
//
// Surfaces:
//   • Total failed (last 30 days) + last-24h pulse
//   • Breakdown by failure_type (llm_config_error / ocr_failed / etc.)
//   • LLM drill-down (provider/model/HTTP code) so the admin knows
//     whether a key rotation or model-name fix is required
//   • Per-document drill-down dialog with a one-click Reprocess
//   • Bulk "Reprocess all llm_config_error" button after the key is fixed
//
// Hidden when there are zero failures — keeps the dashboard quiet under
// normal operation.

const FAILURE_TYPE_LABELS = {
  llm_config_error: { label: 'LLM Config', icon: KeyRound, color: 'text-amber-600' },
  ocr_failed: { label: 'OCR Failed', icon: ServerCrash, color: 'text-orange-600' },
  lambda_exception: { label: 'Lambda Crash', icon: AlertCircle, color: 'text-red-600' },
  sqs_send_failed: { label: 'SQS Failed', icon: AlertCircle, color: 'text-red-600' },
  validation_failed: { label: 'Validation', icon: AlertTriangle, color: 'text-yellow-600' },
  timeout: { label: 'Timeout', icon: AlertCircle, color: 'text-red-600' },
};

function FailureTypeBadge({ type, count }) {
  const meta = FAILURE_TYPE_LABELS[type] || { label: type || 'Other', icon: AlertCircle, color: 'text-muted-foreground' };
  const Icon = meta.icon;
  return (
    <Badge
      variant="outline"
      className="gap-1 font-mono text-[10px]"
      data-testid={`failure-type-badge-${type}`}
    >
      <Icon className={`h-3 w-3 ${meta.color}`} />
      <span>{meta.label}</span>
      <span className="font-bold">{count}</span>
    </Badge>
  );
}

export default function ParsingFailuresTile({ isSuperAdmin }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [failures, setFailures] = useState([]);
  const [failuresLoading, setFailuresLoading] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [reprocessing, setReprocessing] = useState({});
  const [bulkReprocessing, setBulkReprocessing] = useState(false);

  const fetchStats = useCallback(async () => {
    if (!isSuperAdmin) return;
    setLoading(true);
    try {
      const { data } = await api.get('/super-admin/parsing-failures/stats?days=30');
      setStats(data);
    } catch (err) {
      // Silent — tile is best-effort and shouldn't block dashboard load
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin]);

  const fetchFailures = useCallback(async () => {
    setFailuresLoading(true);
    try {
      const params = filterType === 'all' ? '' : `&failure_type=${filterType}`;
      const { data } = await api.get(`/super-admin/parsing-failures?limit=50${params}`);
      setFailures(data.failures || []);
    } catch (err) {
      toast.error('Failed to load parsing failures');
    } finally {
      setFailuresLoading(false);
    }
  }, [filterType]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => {
    if (dialogOpen) fetchFailures();
  }, [dialogOpen, fetchFailures]);

  const handleReprocess = async (docId) => {
    setReprocessing((s) => ({ ...s, [docId]: true }));
    try {
      await api.post(`/super-admin/parsing-failures/${docId}/reprocess`);
      toast.success('Document re-queued for processing');
      // Optimistic: remove from list
      setFailures((prev) => prev.filter((f) => f.id !== docId));
      // Refresh top-line stats so the tile count drops
      fetchStats();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to re-queue document';
      toast.error(msg);
    } finally {
      setReprocessing((s) => ({ ...s, [docId]: false }));
    }
  };

  const handleBulkReprocess = async () => {
    if (!confirm(`Re-queue ALL ${stats?.by_failure_type?.llm_config_error || 0} LLM config-error documents? Use this only AFTER fixing the AI key/model.`)) {
      return;
    }
    setBulkReprocessing(true);
    try {
      const { data } = await api.post('/super-admin/parsing-failures/bulk-reprocess', {
        failure_type: 'llm_config_error',
      });
      toast.success(`Re-queued ${data.succeeded} document(s). ${data.failed} failed.`);
      fetchStats();
      fetchFailures();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk reprocess failed');
    } finally {
      setBulkReprocessing(false);
    }
  };

  if (!isSuperAdmin) return null;
  if (loading) return null;
  if (!stats || stats.total_failed === 0) return null;

  const llmCount = stats.by_failure_type?.llm_config_error || 0;
  const otherCount = stats.total_failed - llmCount;
  const pulseHot = stats.last_24h_count > 0;

  return (
    <>
      <div
        className="border border-amber-200 dark:border-amber-900/50 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 rounded-md p-4"
        data-testid="parsing-failures-tile"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 ${pulseHot ? 'bg-red-500' : 'bg-amber-500'}`}>
              <AlertTriangle className="h-5 w-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-sm font-bold tracking-tight" data-testid="parsing-failures-title">
                  Parsing Failures
                </h3>
                {pulseHot && (
                  <Badge className="bg-red-500 text-white text-[9px] h-4 px-1.5 animate-pulse">
                    {stats.last_24h_count} in last 24h
                  </Badge>
                )}
                <Badge variant="outline" className="text-[10px] font-mono">
                  {stats.window_days}-day window
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                <span className="font-bold text-foreground" data-testid="parsing-failures-total">{stats.total_failed}</span>{' '}
                document{stats.total_failed === 1 ? '' : 's'} failed parsing.{' '}
                {llmCount > 0 && (
                  <span>
                    <span className="font-semibold text-amber-700 dark:text-amber-400">{llmCount}</span>{' '}
                    require AI key/model attention.
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={fetchStats}
              data-testid="parsing-failures-refresh-btn"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="default"
              size="sm"
              className="h-7 text-xs"
              onClick={() => setDialogOpen(true)}
              data-testid="parsing-failures-open-dialog-btn"
            >
              View Details
              <ChevronRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
        </div>

        {/* Failure type breakdown badges */}
        {Object.keys(stats.by_failure_type || {}).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3" data-testid="failure-type-breakdown">
            {Object.entries(stats.by_failure_type).map(([type, count]) => (
              <FailureTypeBadge key={type} type={type} count={count} />
            ))}
          </div>
        )}

        {/* LLM-specific insights — the actionable bit */}
        {stats.llm_breakdown && stats.llm_breakdown.length > 0 && (
          <div className="mt-3 pt-3 border-t border-amber-200/60 dark:border-amber-900/40">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400 mb-1.5">
              LLM Failures — fix in Super Admin → AI Model Setup
            </p>
            <div className="space-y-1" data-testid="llm-breakdown">
              {stats.llm_breakdown.slice(0, 3).map((row, i) => (
                <div key={i} className="flex items-center justify-between text-[11px] font-mono">
                  <span className="text-muted-foreground">
                    <Badge variant="outline" className="h-4 px-1 text-[9px] mr-1.5">HTTP {row.status_code}</Badge>
                    <span className="text-foreground">{row.provider}</span>
                    <span className="text-muted-foreground"> / </span>
                    <span className="text-foreground">{row.model}</span>
                  </span>
                  <span className="font-bold">{row.count}×</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Drill-down dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="parsing-failures-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Parsing Failures — Last 30 Days
            </DialogTitle>
            <DialogDescription>
              Failed documents grouped by failure type. Fix the underlying issue (rotate AI key, fix model name, etc.) in
              <a href="/super-admin?tab=ai" className="text-primary underline mx-1">Super Admin → AI Model Setup</a>
              first, then reprocess.
            </DialogDescription>
          </DialogHeader>

          {/* Filter pills + bulk action */}
          <div className="flex items-center justify-between gap-3 border-b pb-2">
            <div className="flex flex-wrap gap-1.5" data-testid="failure-filter-pills">
              <Button
                variant={filterType === 'all' ? 'default' : 'outline'}
                size="sm"
                className="h-7 text-xs"
                onClick={() => setFilterType('all')}
                data-testid="filter-pill-all"
              >
                All ({stats.total_failed})
              </Button>
              {Object.entries(stats.by_failure_type || {}).map(([type, count]) => (
                <Button
                  key={type}
                  variant={filterType === type ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setFilterType(type)}
                  data-testid={`filter-pill-${type}`}
                >
                  {FAILURE_TYPE_LABELS[type]?.label || type} ({count})
                </Button>
              ))}
            </div>
            {llmCount > 0 && (
              <Button
                variant="default"
                size="sm"
                className="h-7 text-xs bg-amber-600 hover:bg-amber-700"
                onClick={handleBulkReprocess}
                disabled={bulkReprocessing}
                data-testid="bulk-reprocess-llm-btn"
              >
                {bulkReprocessing ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5 mr-1" />
                )}
                Reprocess all {llmCount} LLM failures
              </Button>
            )}
          </div>

          {/* Failures table */}
          <div className="flex-1 overflow-y-auto -mx-6 px-6" data-testid="failures-table-container">
            {failuresLoading ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Loading...
              </div>
            ) : failures.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground" data-testid="no-failures-state">
                <CheckCircle2 className="h-8 w-8 mb-2 text-emerald-500" />
                <p className="text-sm">No failures match this filter.</p>
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-background">
                  <tr className="border-b">
                    <th className="text-left py-2 font-semibold">Document</th>
                    <th className="text-left py-2 font-semibold">Org</th>
                    <th className="text-left py-2 font-semibold">Failure</th>
                    <th className="text-left py-2 font-semibold">When</th>
                    <th className="text-right py-2 font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {failures.map((f) => {
                    const meta = FAILURE_TYPE_LABELS[f.failure_type] || { label: f.failure_type, icon: AlertCircle, color: 'text-muted-foreground' };
                    const Icon = meta.icon;
                    return (
                      <tr key={f.id} className="border-b hover:bg-muted/30" data-testid={`failure-row-${f.id}`}>
                        <td className="py-2 pr-3 max-w-[200px]">
                          <div className="font-mono text-[11px] truncate" title={f.filename}>{f.filename || '—'}</div>
                          <div className="text-[10px] text-muted-foreground font-mono truncate">{f.id.slice(0, 8)}…</div>
                        </td>
                        <td className="py-2 pr-3 text-muted-foreground">{f.organisation_name}</td>
                        <td className="py-2 pr-3">
                          <div className="flex items-center gap-1">
                            <Icon className={`h-3 w-3 ${meta.color}`} />
                            <span className="font-semibold">{meta.label}</span>
                            {f.llm_status_code && (
                              <Badge variant="outline" className="h-4 px-1 text-[9px]">{f.llm_status_code}</Badge>
                            )}
                          </div>
                          {f.llm_model && (
                            <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
                              {f.llm_provider}/{f.llm_model}
                            </div>
                          )}
                          {f.failure_reason && !f.llm_model && (
                            <div className="text-[10px] text-muted-foreground mt-0.5 line-clamp-1" title={f.failure_reason}>
                              {f.failure_reason}
                            </div>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-muted-foreground text-[11px] font-mono">
                          {f.failed_at ? new Date(f.failed_at).toLocaleString() : '—'}
                        </td>
                        <td className="py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <a
                              href={`/documents/${f.id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-muted-foreground hover:text-foreground"
                              data-testid={`open-doc-${f.id}`}
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 text-[10px] px-2"
                              onClick={() => handleReprocess(f.id)}
                              disabled={!f.reprocessable || reprocessing[f.id]}
                              title={!f.reprocessable ? 'Document has no S3 file path — cannot re-queue' : 'Re-queue for parsing'}
                              data-testid={`reprocess-btn-${f.id}`}
                            >
                              {reprocessing[f.id] ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <>
                                  <RefreshCw className="h-3 w-3 mr-1" />
                                  Retry
                                </>
                              )}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
